import { useState } from 'react';
import { useKiosk } from '../contexts/KioskContext';
import { useLocale } from '../hooks/useLocale';
import { submitFeedback } from '../services/feedbackService';
import { clarity } from '../lib/clarity';
import usePageMeta from '../hooks/usePageMeta';
import './FeedbackPage.css';

const EASE_OPTIONS = [
  { value: 'muy_dificil', label: 'Muy difícil' },
  { value: 'dificil', label: 'Difícil' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'facil', label: 'Fácil' },
  { value: 'muy_facil', label: 'Muy fácil' },
];

const TASK_COMPLETED_OPTIONS = [
  { value: 'si_facilmente', label: 'Sí, fácilmente' },
  { value: 'si_con_esfuerzo', label: 'Sí, pero me costó esfuerzo' },
  { value: 'no_desisti', label: 'No, desistí' },
  { value: 'no_aplica', label: 'No aplica' },
];

const OBSTACLE_TRIGGERS = ['si_con_esfuerzo', 'no_desisti'];
const DESIGN_SCALE = [1, 2, 3, 4, 5];
const NPS_SCALE = Array.from({ length: 11 }, (_, i) => i);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ChoiceGroup = ({ options, value, onChange, layout = 'row' }) => (
  <div className={`feedback-choice-group feedback-choice-group--${layout}`} role="group">
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        className={`feedback-choice${value === opt.value ? ' is-selected' : ''}`}
        onClick={() => onChange(opt.value)}
        aria-pressed={value === opt.value}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const ScaleGroup = ({ scale, value, onChange, minLabel, maxLabel }) => (
  <div>
    <div className="feedback-scale-row" role="group">
      {scale.map((n) => (
        <button
          key={n}
          type="button"
          className={`feedback-scale-btn${value === n ? ' is-selected' : ''}`}
          onClick={() => onChange(n)}
          aria-pressed={value === n}
        >
          {n}
        </button>
      ))}
    </div>
    <div className="feedback-scale-labels">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
  </div>
);

// A facilitator's spoken line from the script, shown verbatim so it can be
// read straight off the screen during a live session.
const ScriptLine = ({ children }) => (
  <p className="feedback-script-line">“{children}”</p>
);

const NoteField = ({ id, label, value, onChange, placeholder }) => (
  <div className="feedback-note-field">
    <label htmlFor={id} className="feedback-note-label">{label}</label>
    <textarea
      id={id}
      className="feedback-textarea"
      rows={3}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={2000}
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Step 1: facilitator guide + notes (Guion de Prueba de Usabilidad Moderada)
// ─────────────────────────────────────────────────────────────────────────

const FacilitatorGuideStep = ({ notes, setNote, consentConfirmed, setConsentConfirmed, onContinue }) => (
  <div className="feedback-shell">
    <div className="feedback-page">
      <header className="feedback-header">
        <h1 className="feedback-title">Guion de Prueba de Usabilidad Moderada</h1>
      </header>

      <section className="feedback-script-section">
        <h2 className="feedback-script-title">Preparación / consentimiento</h2>
        <p className="feedback-script-instruction">Confirmar el consentimiento para grabar (pantalla + audio).</p>
        <ScriptLine>No hay respuestas incorrectas — estamos probando la página, no a ti. Por favor, piensa en voz alta mientras avanzas.</ScriptLine>
        <label className="feedback-consent">
          <input
            type="checkbox"
            checked={consentConfirmed}
            onChange={(e) => setConsentConfirmed(e.target.checked)}
          />
          Confirmé el consentimiento para grabar (pantalla + audio)
        </label>
        <p className="feedback-consent-disclaimer">
          Al participar, la persona confirma que acepta los{' '}
          <a href="/terminos-y-condiciones" target="_blank" rel="noopener noreferrer">Términos y Condiciones</a>
          {' '}y el{' '}
          <a href="/terminos-y-condiciones#aviso-de-privacidad" target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>
          {' '}de Bluppimart.
        </p>
      </section>

      <section className="feedback-script-section">
        <h2 className="feedback-script-title">Introducción </h2>
        <ScriptLine>Cuéntame un poco sobre ti y cómo sueles comprar Art Toys o figuras de colección en línea.</ScriptLine>
        <ScriptLine>¿Conoces una página o tienda similar?</ScriptLine>
        <NoteField
          id="fb-notes-intro"
          label="Notas de introducción"
          value={notes.notesIntro}
          onChange={(v) => setNote('notesIntro', v)}
          placeholder="Perfil del participante, experiencia previa..."
        />
      </section>

      <section className="feedback-script-section">
        <h2 className="feedback-script-title">Primera impresión <span className="feedback-script-duration">(5 min)</span></h2>
        <p className="feedback-script-instruction">Muestra la página de inicio. Antes de que hagan clic en algo, pregunta:</p>
        <ScriptLine>¿Para qué crees que es esta página?</ScriptLine>
        <ScriptLine>¿Qué esperarías que pasara si hicieras clic aquí? <span className="feedback-script-aside">(señala un elemento específico)</span></ScriptLine>
        <NoteField
          id="fb-notes-first-impression"
          label="Notas de primera impresión"
          value={notes.notesFirstImpression}
          onChange={(v) => setNote('notesFirstImpression', v)}
        />
      </section>

      <section className="feedback-script-section">
        <h2 className="feedback-script-title">Pruebas basadas en tareas </h2>
        <p className="feedback-script-instruction">Da escenarios realistas, no instrucciones directas. Estructura de ejemplo — adapta las tareas a tus flujos reales:</p>

        <div className="feedback-task-block">
          <p className="feedback-script-instruction"><strong>Tarea 1 (flujo principal — descubrir y agregar al carrito):</strong></p>
          <ScriptLine>Estás buscando una figura de un artista que te guste para agregarla a tu colección. Muéstrame cómo la encontrarías en el catálogo y la agregarías a tu bolsa.</ScriptLine>
          <NoteField id="fb-notes-task1" label="Notas de tarea 1" value={notes.notesTask1} onChange={(v) => setNote('notesTask1', v)} placeholder="¿Usó el catálogo, la página de un artista, la búsqueda?" />
        </div>

        <div className="feedback-task-block">
          <p className="feedback-script-instruction"><strong>Tarea 2 (navegación/facilidad para encontrar — envío):</strong></p>
          <ScriptLine>Quieres saber cuánto cuesta el envío a tu domicilio y si existe la opción de recoger en persona. ¿Dónde buscarías esa información?</ScriptLine>
          <NoteField id="fb-notes-task2" label="Notas de tarea 2" value={notes.notesTask2} onChange={(v) => setNote('notesTask2', v)} placeholder="¿Encontró la diferencia entre envío y recolección en persona?" />
        </div>

        <div className="feedback-task-block">
          <p className="feedback-script-instruction"><strong>Tarea 3 (flujo secundario — completar la compra):</strong></p>
          <ScriptLine>Imagina que ya tienes productos en tu bolsa y quieres completar tu compra. Guíame por el proceso de pago.</ScriptLine>
          <NoteField id="fb-notes-task3" label="Notas de tarea 3" value={notes.notesTask3} onChange={(v) => setNote('notesTask3', v)} placeholder="¿Dudó en algún paso del checkout? ¿Entendió los datos que pedía el formulario?" />
        </div>

        <p className="feedback-script-instruction">Mientras trabajan, pregunta con moderación (sin interrumpir su flujo):</p>
        <ScriptLine>¿Qué estás pensando ahora mismo?</ScriptLine>
        <ScriptLine>¿Qué esperabas que pasara ahí?</ScriptLine>
        <ScriptLine>¿Es esto lo que pensabas que iba a pasar?</ScriptLine>
      </section>

      <section className="feedback-script-section">
        <h2 className="feedback-script-title">Preguntas de cierre <span className="feedback-script-duration">(10 min)</span></h2>
        <ScriptLine>¿Cuál fue la parte más confusa de esta experiencia?</ScriptLine>
        <ScriptLine>¿Qué cambiarías si pudieras cambiar una sola cosa?</ScriptLine>
        <ScriptLine>¿Hay algo que esperabas encontrar y no estaba?</ScriptLine>
        <NoteField id="fb-notes-closing" label="Notas de cierre" value={notes.notesClosing} onChange={(v) => setNote('notesClosing', v)} />
      </section>

      <section className="feedback-script-section">
        <NoteField
          id="fb-notes-general"
          label="Observaciones generales (opcional)"
          value={notes.notesGeneral}
          onChange={(v) => setNote('notesGeneral', v)}
          placeholder="Clics de frustración, dudas, silencios largos..."
        />
      </section>

      <button
        type="button"
        className="feedback-submit"
        disabled={!consentConfirmed}
        onClick={onContinue}
      >
        Continuar al cuestionario →
      </button>
      {!consentConfirmed && (
        <p className="feedback-hint">Confirma el consentimiento arriba para continuar.</p>
      )}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Step 2: Cuestionario de Feedback en la Página
// ─────────────────────────────────────────────────────────────────────────

const FeedbackPage = () => {
  usePageMeta({
    title: 'Feedback — Bluppimart',
    description: 'Cuéntanos tu experiencia en Bluppimart.',
  });
  const { isKiosk } = useKiosk();
  const locale = useLocale();

  const [step, setStep] = useState('guide'); // 'guide' | 'questionnaire'
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [notes, setNotes] = useState({
    notesIntro: '',
    notesFirstImpression: '',
    notesTask1: '',
    notesTask2: '',
    notesTask3: '',
    notesClosing: '',
    notesGeneral: '',
  });
  const setNote = (key, value) => setNotes((n) => ({ ...n, [key]: value }));

  const [easeOfFinding, setEaseOfFinding] = useState(null);
  const [taskDescription, setTaskDescription] = useState('');
  const [taskCompleted, setTaskCompleted] = useState(null);
  const [obstacle, setObstacle] = useState('');
  const [designRating, setDesignRating] = useState(null);
  const [npsScore, setNpsScore] = useState(null);
  const [improvementSuggestion, setImprovementSuggestion] = useState('');
  const [wantsCall, setWantsCall] = useState(null); // null | true | false — question itself is optional
  const [contactEmail, setContactEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const obstacleRequired = OBSTACLE_TRIGGERS.includes(taskCompleted);
  const emailRequired = wantsCall === true;
  const emailValid = !emailRequired || EMAIL_RE.test(contactEmail.trim());

  const canSubmit =
    !!easeOfFinding &&
    taskDescription.trim().length > 0 &&
    !!taskCompleted &&
    (!obstacleRequired || obstacle.trim().length > 0) &&
    !!designRating &&
    emailValid &&
    !submitting;

  const handleContinueToQuestionnaire = () => {
    if (!consentConfirmed) return;
    setStep('questionnaire');
    window.scrollTo({ top: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitFeedback({
        easeOfFinding,
        taskDescription: taskDescription.trim(),
        taskCompleted,
        obstacle: obstacleRequired ? obstacle.trim() : undefined,
        designRating,
        npsScore: npsScore === null ? undefined : npsScore,
        improvementSuggestion: improvementSuggestion.trim() || undefined,
        wantsCall: wantsCall === true,
        contactEmail: wantsCall === true ? contactEmail.trim() : undefined,
        pageUrl: document.referrer || window.location.href,
        locale,
        source: isKiosk ? 'kiosk' : 'web',
        consentConfirmed,
        notesIntro: notes.notesIntro.trim() || undefined,
        notesFirstImpression: notes.notesFirstImpression.trim() || undefined,
        notesTask1: notes.notesTask1.trim() || undefined,
        notesTask2: notes.notesTask2.trim() || undefined,
        notesTask3: notes.notesTask3.trim() || undefined,
        notesClosing: notes.notesClosing.trim() || undefined,
        notesGeneral: notes.notesGeneral.trim() || undefined,
      });
      clarity.event('usability_feedback_submitted');
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Error al enviar tu respuesta.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="feedback-shell">
        <div className="feedback-page feedback-page--thanks">
          <h1 className="feedback-title">¡Gracias por tu feedback!</h1>
          <p className="feedback-thanks-body">Tu respuesta nos ayuda a mejorar Bluppimart.</p>
        </div>
      </div>
    );
  }

  if (step === 'guide') {
    return (
      <FacilitatorGuideStep
        notes={notes}
        setNote={setNote}
        consentConfirmed={consentConfirmed}
        setConsentConfirmed={setConsentConfirmed}
        onContinue={handleContinueToQuestionnaire}
      />
    );
  }

  return (
    <div className="feedback-shell">
      <form className="feedback-page" onSubmit={handleSubmit}>
        <header className="feedback-header">
          <button type="button" className="feedback-back" onClick={() => setStep('guide')}>
            ← Volver al guion
          </button>
          <h1 className="feedback-title">Cuéntanos tu experiencia</h1>
          <p className="feedback-subtitle">Toma menos de 2 minutos y nos ayuda muchísimo.</p>
        </header>

        <section className="feedback-question">
          <label className="feedback-label">
            1. En general, ¿qué tan fácil fue encontrar lo que buscabas hoy? <span className="feedback-required">*</span>
          </label>
          <ChoiceGroup options={EASE_OPTIONS} value={easeOfFinding} onChange={setEaseOfFinding} layout="row" />
        </section>

        <section className="feedback-question">
          <label className="feedback-label" htmlFor="fb-task">
            2. ¿Qué estabas tratando de hacer en esta página? <span className="feedback-required">*</span>
          </label>
          <input
            id="fb-task"
            className="feedback-input"
            type="text"
            value={taskDescription}
            onChange={(e) => setTaskDescription(e.target.value)}
            maxLength={500}
            placeholder="Ej. buscar productos de un artista en específico"
          />
        </section>

        <section className="feedback-question">
          <label className="feedback-label">
            3. ¿Pudiste completar esa tarea? <span className="feedback-required">*</span>
          </label>
          <ChoiceGroup options={TASK_COMPLETED_OPTIONS} value={taskCompleted} onChange={setTaskCompleted} layout="column" />
        </section>

        {obstacleRequired && (
          <section className="feedback-question feedback-question--conditional">
            <label className="feedback-label" htmlFor="fb-obstacle">
              Si tuviste dificultades, ¿qué se interpuso en tu camino? <span className="feedback-required">*</span>
            </label>
            <input
              id="fb-obstacle"
              className="feedback-input"
              type="text"
              value={obstacle}
              onChange={(e) => setObstacle(e.target.value)}
              maxLength={500}
            />
          </section>
        )}

        <section className="feedback-question">
          <label className="feedback-label">
            4. ¿Cómo calificarías el diseño y la disposición general de esta página? <span className="feedback-required">*</span>
          </label>
          <ScaleGroup scale={DESIGN_SCALE} value={designRating} onChange={setDesignRating} minLabel="1 = Deficiente" maxLabel="5 = Excelente" />
        </section>

        <section className="feedback-question">
          <label className="feedback-label">
            5. ¿Qué tan probable es que recomiendes este sitio a un amigo o colega? <span className="feedback-optional">(opcional)</span>
          </label>
          <ScaleGroup scale={NPS_SCALE} value={npsScore} onChange={setNpsScore} minLabel="0 = Nada probable" maxLabel="10 = Muy probable" />
        </section>

        <section className="feedback-question">
          <label className="feedback-label" htmlFor="fb-improve">
            6. ¿Qué es una cosa que podríamos mejorar? <span className="feedback-optional">(opcional)</span>
          </label>
          <input
            id="fb-improve"
            className="feedback-input"
            type="text"
            value={improvementSuggestion}
            onChange={(e) => setImprovementSuggestion(e.target.value)}
            maxLength={1000}
          />
        </section>

        <section className="feedback-question">
          <label className="feedback-label">
            7. ¿Estarías dispuesto/a a una llamada de 15 minutos para compartir más feedback? <span className="feedback-optional">(opcional)</span>
          </label>
          <div className="feedback-choice-group feedback-choice-group--row" role="group">
            <button
              type="button"
              className={`feedback-choice${wantsCall === true ? ' is-selected' : ''}`}
              onClick={() => setWantsCall(true)}
              aria-pressed={wantsCall === true}
            >
              Sí
            </button>
            <button
              type="button"
              className={`feedback-choice${wantsCall === false ? ' is-selected' : ''}`}
              onClick={() => { setWantsCall(false); setContactEmail(''); }}
              aria-pressed={wantsCall === false}
            >
              No
            </button>
          </div>
          {wantsCall === true && (
            <input
              className="feedback-input feedback-input--email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="tu@correo.com"
              autoComplete="email"
            />
          )}
        </section>

        {error && <p className="feedback-error">{error}</p>}

        <button type="submit" className="feedback-submit" disabled={!canSubmit}>
          {submitting ? 'Enviando…' : 'Enviar respuesta'}
        </button>
      </form>
    </div>
  );
};

export default FeedbackPage;
